import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { asList } from '../utils/asList';
import { formatSsLabel } from '../utils/unitDisplay';
import { ssCodeBare } from '../utils/unitIdFormat';
import {
  catalogSubNumber,
  isDefaultCategory,
  maxCatalogSubNumber,
  orderCatalogCategories,
  sameEquipmentType,
} from '../utils/equipmentTypeCatalog';
import { getSsHandbookInfo } from '../data/ssHandbookInfo';

/**
 * Pick SS sub-types and Save — registers them on the building and refreshes the list in place.
 * Custom types appear in the list after Save; number advances and the name field clears.
 */
export default function SsUnitSubtypeWizard({
  buildingId,
  ssCode,
  instanceId,
  units = [],
  setupList = false,
  onShowList,
  onBackToPick,
  onOpenUnit,
}) {
  const queryClient = useQueryClient();
  const ssLabel = formatSsLabel(ssCode);
  const bare = ssCodeBare(ssCode);
  const handbook = getSsHandbookInfo(ssCode);

  const { data: categories = [], isLoading: catsLoading } = useQuery({
    queryKey: ['checklist-categories', ssCode],
    queryFn: async () => {
      const res = await api.get('/checklist-sets/categories', { params: { ssCode } });
      return asList(res.data?.data ?? res.data);
    },
    enabled: !!ssCode,
  });

  const { data: registeredTypes = [] } = useQuery({
    queryKey: ['ss-equipment-types', buildingId, instanceId],
    queryFn: async () => {
      const res = await api.get(
        `/buildings/${buildingId}/ss-instances/${instanceId}/equipment-types`
      );
      return asList(res.data?.data ?? res.data);
    },
    enabled: !!buildingId && !!instanceId,
  });

  const orderedCats = useMemo(() => orderCatalogCategories(categories), [categories]);

  const defaultRow = useMemo(() => {
    const cat = orderedCats.find(isDefaultCategory) || {
      category_key: 'common',
      label: 'Default',
      sort_order: 0,
    };
    return {
      number: 1,
      key: String(cat.category_key || 'common'),
      label: cat.label || 'Default',
      isCustom: false,
      locked: true,
    };
  }, [orderedCats]);

  const optionalRows = useMemo(() => {
    return orderedCats
      .filter((c) => !isDefaultCategory(c))
      .map((c) => ({
        number: catalogSubNumber(c, categories),
        key: String(c.category_key || c.label),
        label: c.label || c.category_key || 'Type',
        isCustom: false,
        locked: false,
      }));
  }, [orderedCats, categories]);

  const isRegistered = (row) => registeredTypes.some((t) => sameEquipmentType(t, row));

  /** Custom (or non-catalog) types already on this SS — show in the list after Save. */
  const extraRegisteredRows = useMemo(() => {
    return registeredTypes
      .filter((t) => {
        const n = Number(t.sub_number);
        if (!Number.isFinite(n) || n < 1) return false;
        if (n === 1) return false;
        const key = String(t.category_key || '').toLowerCase();
        const inCatalog = optionalRows.some(
          (r) =>
            Number(r.number) === n ||
            String(r.key).toLowerCase() === key ||
            String(r.label).toLowerCase() === String(t.label || '').toLowerCase()
        );
        return !inCatalog;
      })
      .map((t) => ({
        number: Number(t.sub_number),
        key: String(t.category_key || t.label || t.id),
        label: t.label || t.category_key || 'Custom',
        isCustom: true,
        locked: true,
        registered: true,
        typeId: t.id,
      }))
      .sort((a, b) => a.number - b.number);
  }, [registeredTypes, optionalRows]);

  const nextCustomNumber = useMemo(() => {
    const catalogMax = maxCatalogSubNumber(categories) || 1;
    const used = new Set([
      ...registeredTypes.map((t) => Number(t.sub_number)),
      catalogMax,
    ]);
    let n = catalogMax + 1;
    while (used.has(n)) n += 1;
    return n;
  }, [categories, registeredTypes]);

  const [checked, setChecked] = useState(() => new Set());
  const [wantNew, setWantNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [pickedItems, setPickedItems] = useState([]);

  useEffect(() => {
    setChecked((prev) => {
      const next = new Set();
      for (const row of optionalRows) {
        if (isRegistered(row) || prev.has(row.key)) next.add(row.key);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when server types change
  }, [registeredTypes, optionalRows]);

  const registerType = useMutation({
    mutationFn: async ({ number, label, key, isCustom }) => {
      const existing = registeredTypes.find((t) =>
        sameEquipmentType(t, { key, label, number })
      );
      if (existing) return existing;
      const res = await api.post(
        `/buildings/${buildingId}/ss-instances/${instanceId}/equipment-types`,
        {
          sub_number: number,
          label,
          category_key: key || label,
          is_custom: !!isCustom,
        },
        { skipErrorToast: true }
      );
      return res.data?.data ?? res.data;
    },
  });

  const createUnit = useMutation({
    mutationFn: async (equipmentType) => {
      const prefix = equipmentType?.prefix || `${bare}-${equipmentType?.sub_number}`;
      const found = (units || []).find((u) => {
        if (equipmentType?.id && String(u.equipment_type_id) === String(equipmentType.id)) {
          return true;
        }
        const uid = String(u.unique_id || '');
        return uid === prefix || uid.startsWith(`${prefix}-`);
      });
      if (found) return found;
      const res = await api.post(
        `/buildings/${buildingId}/ss-instances/${instanceId}/units`,
        {
          equipment_type_id: equipmentType.id,
          unit_type: equipmentType.label,
          name: equipmentType.label,
          reuse_existing: true,
        },
        { skipErrorToast: true }
      );
      return res.data?.data ?? res.data;
    },
  });

  const buildItems = () => {
    const items = [defaultRow];
    for (const row of optionalRows) {
      if (checked.has(row.key)) items.push(row);
    }
    for (const row of extraRegisteredRows) {
      if (!items.some((i) => Number(i.number) === Number(row.number))) items.push(row);
    }
    if (wantNew) {
      const label = newName.trim();
      if (!label) return { error: 'Enter a name for the new unit type' };
      if (
        items.some((i) => String(i.label).toLowerCase() === label.toLowerCase()) ||
        registeredTypes.some((t) => String(t.label || '').toLowerCase() === label.toLowerCase())
      ) {
        return { error: `"${label}" is already on this SS` };
      }
      items.push({
        number: nextCustomNumber,
        key: `custom:${label}`,
        label,
        isCustom: true,
      });
    }
    return { items };
  };

  const ensureTypeAndUnit = async (item) => {
    const typeRow = await registerType.mutateAsync(item);
    const unit = await createUnit.mutateAsync(typeRow);
    return { typeRow, unit };
  };

  /** Persist selected types to DB and stay on this screen with a refreshed list. */
  const handleSave = async () => {
    if (saveLock.current) return;
    if (!instanceId) {
      toast.error('This SS is not on the building yet');
      return;
    }
    saveLock.current = true;
    const built = buildItems();
    if (built.error) {
      toast.error(built.error);
      saveLock.current = false;
      return;
    }
    const items = built.items;
    try {
      for (const item of items) {
        await ensureTypeAndUnit(item);
      }
      await queryClient.invalidateQueries(['ss-equipment-types', buildingId, instanceId]);
      await queryClient.invalidateQueries(['building', buildingId, 'ss-units']);
      await queryClient.invalidateQueries(['building', buildingId, 'ss-instances']);
      setPickedItems(items);
      setNewName('');
      // Keep Yes selected so the next number + empty name are ready for another custom type
      if (wantNew) setWantNew(true);
      toast.success(
        items.length === 1
          ? `Saved ${items[0].label}`
          : `Saved ${items.length} unit types`
      );
      // Do not navigate to setup=list — list updates in place above
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Could not save unit types');
    } finally {
      saveLock.current = false;
    }
  };

  const handleOpen = async (item) => {
    if (!instanceId) {
      toast.error('This SS is not on the building yet');
      return;
    }
    try {
      const { typeRow, unit } = await ensureTypeAndUnit(item);
      await queryClient.invalidateQueries(['ss-equipment-types', buildingId, instanceId]);
      await queryClient.invalidateQueries(['building', buildingId, 'ss-units']);
      const unitId = unit?.id;
      if (!unitId) {
        toast.error('Could not open unit');
        return;
      }
      const code = typeRow?.prefix || `${bare}-${item.number}`;
      onOpenUnit?.(unitId, code, item.label);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Could not open unit setting');
    }
  };

  const handleSaveOne = async (item) => {
    if (!instanceId) {
      toast.error('This SS is not on the building yet');
      return;
    }
    try {
      await ensureTypeAndUnit(item);
      await queryClient.invalidateQueries(['ss-equipment-types', buildingId, instanceId]);
      await queryClient.invalidateQueries(['building', buildingId, 'ss-units']);
      toast.success(`Saved ${item.label}`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Could not save this type');
    }
  };

  const handleDeleteCustom = async (row) => {
    if (!instanceId || !row?.typeId) {
      toast.error('Cannot delete this type');
      return;
    }
    const ok = window.confirm(
      `Delete custom sub "${row.label}" (${bare}-${row.number})?\n\n` +
        'Units of this type will be removed from the active list. This cannot be undone from here.'
    );
    if (!ok) return;
    try {
      await api.delete(
        `/buildings/${buildingId}/ss-instances/${instanceId}/equipment-types/${row.typeId}`
      );
      await queryClient.invalidateQueries(['ss-equipment-types', buildingId, instanceId]);
      await queryClient.invalidateQueries(['building', buildingId, 'ss-units']);
      await queryClient.invalidateQueries(['building', buildingId, 'ss-instances']);
      toast.success(`Deleted ${row.label}`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Could not delete this type');
    }
  };

  const busy = registerType.isPending || createUnit.isPending;
  const saveLock = useRef(false);

  // Legacy setup=list URL — keep a simple back path; primary flow no longer uses Next
  if (setupList) {
    const items = pickedItems.length
      ? pickedItems
      : [
          defaultRow,
          ...optionalRows.filter((r) => isRegistered(r)),
          ...extraRegisteredRows,
        ];
    if (items.length === 0) {
      return (
        <div className="bg-card rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">No types selected yet.</p>
          <button
            type="button"
            onClick={onBackToPick}
            className="mt-3 px-4 py-2 border rounded-lg text-sm"
          >
            Back
          </button>
        </div>
      );
    }
    return (
      <div className="bg-card rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <p className="text-sm font-semibold text-gray-900">{ssLabel} types</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Open a type to edit settings, or go Back to the checklist.
          </p>
        </div>
        <ul className="divide-y">
          {items.map((item) => {
            const registered = isRegistered(item) || item.registered;
            return (
              <li
                key={item.key}
                className="px-4 py-3 flex flex-wrap items-center gap-2 hover:bg-slate-50/80"
              >
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleOpen(item)}
                  className="flex-1 min-w-[12rem] text-left flex items-baseline gap-3 disabled:opacity-60"
                >
                  <span className="font-mono text-sm font-semibold text-primary-800 w-16 shrink-0">
                    {bare}-{item.number}
                  </span>
                  <span className="text-sm text-gray-900">{item.label}</span>
                  {registered ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700">
                      Saved
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleSaveOne(item)}
                  className="px-3 py-1.5 rounded-lg border border-primary-300 text-primary-800 text-xs font-semibold hover:bg-primary-50 disabled:opacity-50"
                >
                  Save
                </button>
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-3 border-t bg-gray-50 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onBackToPick}
            className="px-4 py-2 border rounded-lg text-sm"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <div className="px-4 py-4 border-b">
        <p className="text-base font-semibold text-gray-900">
          {handbook?.title || ssLabel}
        </p>
        {!handbook?.title && (
          <p className="text-sm text-gray-600 mt-1">{ssLabel}</p>
        )}
      </div>

      <div className="p-4 space-y-5">
        {catsLoading ? (
          <p className="text-sm text-gray-500">Loading types…</p>
        ) : (
          <div>
            <p className="text-sm font-medium text-gray-800 mb-2">Select sub-types for this SS</p>
            <ul className="space-y-1.5">
              <li>
                <label className="flex items-start gap-3 py-1.5">
                  <input type="checkbox" className="mt-1" checked disabled />
                  <span className="text-sm text-gray-900">
                    <span className="font-mono font-semibold text-primary-800 mr-2">1.</span>
                    {defaultRow.label}
                    <span className="ml-2 text-[11px] text-gray-500">(always included)</span>
                  </span>
                </label>
              </li>
              {optionalRows.map((row) => {
                const already = isRegistered(row);
                return (
                  <li key={row.key}>
                    <label className="flex items-start gap-3 py-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked.has(row.key) || already}
                        disabled={already}
                        onChange={(e) => {
                          if (already) return;
                          setChecked((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(row.key);
                            else next.delete(row.key);
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm text-gray-900">
                        <span className="font-mono font-semibold text-primary-800 mr-2">
                          {row.number}.
                        </span>
                        {row.label}
                        {already ? (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700">
                            Saved
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
              {extraRegisteredRows.map((row) => (
                <li key={`extra-${row.key}`} className="flex items-start gap-2 py-1.5">
                  <label className="flex items-start gap-3 flex-1 min-w-0">
                    <input type="checkbox" className="mt-1" checked disabled />
                    <span className="text-sm text-gray-900">
                      <span className="font-mono font-semibold text-primary-800 mr-2">
                        {row.number}.
                      </span>
                      {row.label}
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700">
                        Saved
                      </span>
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-500">
                        Custom
                      </span>
                    </span>
                  </label>
                  <button
                    type="button"
                    disabled={busy || !row.typeId}
                    onClick={() => handleDeleteCustom(row)}
                    className="shrink-0 mt-0.5 px-2.5 py-1 rounded-md border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-50"
                    title="Delete this custom sub-type"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-gray-500 mt-2">
              If you select nothing extra, only Default ({bare}-1) is used. Numbers are fixed by
              catalog (e.g. Fire pump stays {bare}-4 even if Dry is unused).
            </p>
          </div>
        )}

        <div className="border-t pt-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">Is there a new unit type?</p>
          <div className="flex items-center gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="wantNewType"
                checked={!wantNew}
                onChange={() => setWantNew(false)}
              />
              No
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="wantNewType"
                checked={wantNew}
                onChange={() => setWantNew(true)}
              />
              Yes
            </label>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-gray-600">
              Number
              <input
                readOnly
                disabled={!wantNew}
                value={bare ? `${bare}-${nextCustomNumber}` : String(nextCustomNumber)}
                className="mt-0.5 block w-28 px-2 py-2 border rounded-lg font-mono text-sm bg-gray-50 disabled:text-gray-400"
              />
            </label>
            <label className="text-xs text-gray-600 flex-1 min-w-[12rem]">
              Name
              <input
                disabled={!wantNew}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New unit type name"
                className="mt-0.5 block w-full px-3 py-2 border rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-400"
              />
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            id="ss-unit-subtype-save"
            type="button"
            disabled={busy || catsLoading}
            onClick={handleSave}
            className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
